import { createEvent } from 'effector';
import type { GeneralID } from '@types';
import { $categoryListApi, $categoryMapApi } from '@stores/dataStores';
import arrayMove from '@utils/arrayMove';
import type { DragItemScenes } from '../ScenesManagementModal/dnd';

function arrangeItems(
  items: DragItemScenes['ids']
): { parentId: number; children: GeneralID[] }[] {
  const parentIdMap = new Map<number, GeneralID[]>();

  items.forEach((item) => {
    if (!parentIdMap.has(item.parentId)) {
      parentIdMap.set(item.parentId, []);
    }
    parentIdMap.get(item.parentId)?.push(item.id);
  });

  const target = Array.from(parentIdMap, ([parentId, children]) => ({
    parentId,
    children,
  }));
  return target;
}

export const dragScenes = createEvent<{
  targetId: GeneralID;
  pos: 'top' | 'bottom';
  ids: DragItemScenes['ids'];
  categoryId: number;
}>();
dragScenes.watch(({ targetId, pos, ids, categoryId }) => {
  $categoryMapApi.update((draft) => {
    const arranged = arrangeItems(ids);
    arranged.forEach(({ parentId, children }) => {
      const category = draft.get(parentId)!;
      category.sceneIds = category.sceneIds.filter(
        (id) => !children.includes(id)
      );
    });
    const category = draft.get(categoryId)!;
    let index = category.sceneIds.indexOf(targetId);
    if (pos === 'bottom') index += 1;
    category.sceneIds.splice(index, 0, ...ids.map((x) => x.id));
  });
});

export const dragScenesToCategory = createEvent<{
  ids: DragItemScenes['ids'];
  categoryId: number;
}>();
dragScenesToCategory.watch(({ ids, categoryId }) => {
  $categoryMapApi.update((draft) => {
    const arranged = arrangeItems(ids);
    arranged.forEach(({ parentId, children }) => {
      const category = draft.get(parentId)!;
      category.sceneIds = category.sceneIds.filter(
        (id) => !children.includes(id)
      );
    });
    const category = draft.get(categoryId)!;
    category.sceneIds.push(...ids.map((x) => x.id));
  });
});

export const dragSubCategory = createEvent<{
  id: number;
  parentId: number;
  targetParentId: number;
  targetId: number;
  pos: 'top' | 'bottom';
}>();
dragSubCategory.watch(({ id, parentId, targetParentId, targetId, pos }) => {
  $categoryMapApi.update((draft) => {
    const fromCategory = draft.get(parentId)!;
    fromCategory.categoryIds = fromCategory.categoryIds.filter(
      (_id) => _id !== id
    );
    const toCategory = draft.get(targetParentId)!;
    let index = toCategory.categoryIds.indexOf(targetId);
    if (pos === 'bottom') index += 1;
    toCategory.categoryIds.splice(index, 0, id);
  });
});

export const dragSubCategoryToCategory = createEvent<{
  id: number;
  parentId: number;
  targetCategoryId: number;
}>();
dragSubCategoryToCategory.watch(({ id, parentId, targetCategoryId }) => {
  $categoryMapApi.update((draft) => {
    const fromCategory = draft.get(parentId)!;
    fromCategory.categoryIds = fromCategory.categoryIds.filter(
      (_id) => _id !== id
    );
    const toCategory = draft.get(targetCategoryId)!;
    toCategory.categoryIds.push(id);
  });
});

export const dragCategory = createEvent<{
  id: number;
  targetId: number;
  pos: 'top' | 'bottom';
}>();
dragCategory.watch(({ id, targetId, pos }) => {
  $categoryListApi.update((draft) => {
    const fromIndex = draft.indexOf(id);
    const toIndex = draft.indexOf(targetId);
    return arrayMove({
      array: draft,
      fromIndex,
      toIndex,
    });
  });
});
